/**
 * PanelCurve — the draggable elevation-curve editor, in the card's language.
 *
 * Lifted out of NewWorldDialog BEHAVIOURALLY UNCHANGED and restyled: points map the continental
 * noise (x: 0..1, left to right) to a normalized height offset (y: -1 ocean floor .. +1 peak); the
 * line drawn is the actual monotone-cubic curve the generator evaluates, sampled from `Curve`, not
 * an approximation of it; end points move only vertically, and an interior point stays ordered
 * between its neighbours.
 */

import { useRef, useState } from 'react';
import { Curve, type CurvePoint } from '@worldify/shared';
import { GREEN, GREEN_DEEP, MOTION } from './tokens';

interface PanelCurveProps {
  points: CurvePoint[];
  onChange: (points: CurvePoint[]) => void;
  onReset?: () => void;
  /** The dashed reference line, in curve space. Sea level is 0. */
  referenceLabel?: string;
}

const W = 264, H = 120, PAD = 10;

export function PanelCurve({ points, onChange, onReset, referenceLabel = 'sea' }: PanelCurveProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<number | null>(null);

  const px = (x: number) => PAD + x * (W - 2 * PAD);
  const py = (y: number) => PAD + (1 - (y + 1) / 2) * (H - 2 * PAD);
  const move = (clientX: number, clientY: number) => {
    if (drag === null || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const cx = clientX - r.left, cy = clientY - r.top;
    const x = Math.min(1, Math.max(0, (cx - PAD) / (W - 2 * PAD)));
    const y = Math.min(1, Math.max(-1, (1 - (cy - PAD) / (H - 2 * PAD)) * 2 - 1));
    const pts = points.map((p) => ({ ...p }));
    const isEnd = drag === 0 || drag === pts.length - 1;
    pts[drag].y = y;
    if (!isEnd) pts[drag].x = Math.min(pts[drag + 1].x - 0.02, Math.max(pts[drag - 1].x + 0.02, x));
    onChange(pts);
  };

  const curve = new Curve(points);
  let path = '';
  for (let i = 0; i <= 40; i++) {
    const x = i / 40;
    path += `${i === 0 ? 'M' : 'L'} ${px(x).toFixed(1)} ${py(curve.eval(x)).toFixed(1)} `;
  }
  const refY = py(0);

  return (
    <div className="flex flex-col gap-1">
      <svg
        ref={svgRef}
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        className="bg-neutral-100 rounded-2xl ring-1 ring-inset ring-black/5 touch-none select-none"
        onPointerMove={(e) => { if (drag !== null) { e.preventDefault(); move(e.clientX, e.clientY); } }}
        onPointerUp={() => setDrag(null)}
        onPointerLeave={() => setDrag(null)}
      >
        <line x1={PAD} y1={refY} x2={W - PAD} y2={refY} stroke="rgba(2,92,59,0.35)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={W - PAD} y={refY - 3} textAnchor="end" fontSize="8" fill="rgba(2,92,59,0.6)">{referenceLabel}</text>
        <path d={path} fill="none" stroke={GREEN_DEEP} strokeWidth="2" />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={px(p.x)} cy={py(p.y)} r={5}
            fill={drag === i ? GREEN_DEEP : GREEN}
            stroke="white" strokeWidth="2"
            className={`cursor-grab ${MOTION}`}
            onPointerDown={(e) => {
              e.preventDefault();
              (e.target as Element).setPointerCapture?.(e.pointerId);
              setDrag(i);
            }}
          />
        ))}
      </svg>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] leading-tight text-neutral-400">
          Drag points: left to right is low to high land.
        </span>
        {onReset && (
          <button type="button" onClick={onReset} className="text-[10px] text-neutral-400 hover:text-neutral-700 underline cursor-pointer">
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
